import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeSlash } from '@phosphor-icons/react';

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";
const HERO_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_medform-standby/artifacts/5kmwzsdg_WhatsApp%20Image%202026-07-26%20at%2010.35.32%20AM.jpeg";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left — Form Side */}
      <div className="flex flex-col justify-center bg-white px-8 py-16 lg:px-16 xl:px-24">
        <div className="w-full max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
          {/* Logos */}
          <div className="flex items-center gap-6 mb-16">
            <img src={LOGO_1} alt="Stand By Site Medical Services" className="h-14 object-contain" data-testid="logo-standby" />
            <div className="w-px h-10 bg-slate-200" />
            <img src={LOGO_2} alt="On-Site Medical Consultants" className="h-14 object-contain" data-testid="logo-onsite" />
          </div>

          {/* Title */}
          <div className="mb-10">
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Event Triage
            </h1>
            <p className="mt-3 text-base text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sign in to access the patient management system
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-900 uppercase tracking-[0.2em]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Username / Email
              </Label>
              <Input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your username or email"
                required
                className="h-12 rounded-sm border-slate-200 focus:ring-2 focus:ring-slate-900"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-900 uppercase tracking-[0.2em]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Password
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-12 rounded-sm border-slate-200 focus:ring-2 focus:ring-slate-900 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
                data-testid="forgot-password-link"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-medium tracking-wide"
              disabled={loading}
              data-testid="login-submit-button"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Event Triage &copy; {new Date().getFullYear()} &mdash; Stand By Site Medical Services
          </p>
        </div>
      </div>

      {/* Right — Hero Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Medical standby event"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-slate-900/40" />
        {/* Bottom text overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-10 bg-gradient-to-t from-slate-900/80 to-transparent">
          <p className="text-white/90 text-lg font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Professional Medical Standby Services
          </p>
          <p className="text-white/60 text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Real-time patient triage, documentation, and command post operations
          </p>
        </div>
      </div>
    </div>
  );
}
